import _ from 'lodash';

import {
  ImportOrderInput,
  ImportOrderLineItemInput,
  ImportOrdersFromCsvInput,
  ImportPropertyValue,
  OrderPropertyName,
  useImportOrdersFromCsvMutation,
  useImportOrdersModalQuery,
  usePreValidateCsvImportLazyQuery
} from 'generated/graphql';
import {
  useCallback,
  useMemo
} from 'react';

import { ImportCsvModal } from 'iron-forge-react-modules';



export type IColumnDef = ImportCsvModal.IColumnDescription<OrderPropertyName>;

export interface IData {
  orderPrefix: string;
  prefixNewOrders: boolean;
  sourceLabel2PropertyNames: _.Dictionary<OrderPropertyName[]>;
  propertyName2DefaultValue: Record<OrderPropertyName, string>;
  propertyName2SourceLabel: Record<OrderPropertyName, string>;
  columnDefs: IColumnDef[];
}

export function useQuery(storeId: string) {
  const tuple = useImportOrdersModalQuery({
    variables: {
      storeId
    }
  });



  return useMemo(
    () => {
      const propMap = tuple.data?.store?.customOrderFormatFlow?.propertyMapping ?? [];
      const propName2Mapping = _.keyBy(propMap, m => m.mapToProperty.name);
      const sourceLabel2MappingRows = _.groupBy(propMap, pm => pm.sourceDataLabel);

      const data: IData = {
        orderPrefix: tuple.data?.store?.orderPrefix ?? '',
        prefixNewOrders: tuple.data?.store?.storeSettings.prefixNewOrders ?? false,
        sourceLabel2PropertyNames: _.mapValues(sourceLabel2MappingRows, pm => _.map(pm, p => p.mapToProperty.name)),

        propertyName2SourceLabel: _.mapValues(
          propName2Mapping,
          pm => pm.sourceDataLabel) as Record<OrderPropertyName, string>,

        propertyName2DefaultValue: _.mapValues(
          propName2Mapping,
          m => m.defaultValueOnEmpty) as Record<OrderPropertyName, string>,

        columnDefs: _.map(
          propMap,
          (mapping): IColumnDef => ({
            id: mapping.sourceDataLabel,
            label: mapping.mapToProperty.displayName,
            isRequired: _.isEmpty(mapping.defaultValueOnEmpty),
            formatType: mapping.mapToProperty.name
          }))
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useImportOrdersFromCsv(storeId: string, prefixOrders: boolean) {
  const [fnMutate, tuple] = useImportOrdersFromCsvMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = useCallback(
    (rows: Array<_.Dictionary<string>>, data: IData) => {
      const input = __prepareForImport({
        rawCsvRows: rows,
        data,
        storeId,
        prefixOrders
      });

      fnMutate({
        variables: {
          input
        }
      });
    },
    [fnMutate, prefixOrders, storeId]
  );

  return useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function usePreValidateCsvImport(storeId: string, prefixOrders: boolean) {
  const [fnQuery, tuple] = usePreValidateCsvImportLazyQuery({
    fetchPolicy: 'network-only'
  });

  const fnWrappedQuery = useCallback(
    async (rows: Array<_.Dictionary<string>>, data: IData) => {
      const input = __prepareForImport({
        rawCsvRows: rows,
        data,
        storeId,
        prefixOrders
      });

      const ordersWithNumbersGte25Characters = _.filter(input.orders, order =>
        (_.find(order.detail, det => det.propertyName === 'order_identifier')?.value.length ?? 0) > 25);
      const nums = _.map(ordersWithNumbersGte25Characters, order => _.find(order.detail, d => d.propertyName === 'order_identifier')?.value);

      if (!_.isEmpty(ordersWithNumbersGte25Characters)) {
        const joinedOrderNumbers = _.join(_.compact(nums), '\n');
        alert(`The following orders have numbers greater than 25 characters (E1 limit): ${joinedOrderNumbers}`);
        return;
      }

      const orderNumber2Details = _.keyBy(
        input.orders,
        order => _.find(order.detail, det => det.propertyName === 'order_identifier')?.value ?? ''
      );

      const orderNumber2Email = _.mapValues(
        orderNumber2Details,
        val => _.find(val.detail, det => det.propertyName === 'customer_email')?.value ?? ''
      );

      const emails = _.values(orderNumber2Email);
      const uniqueEmails = _.uniq(emails);

      if (emails.length !== uniqueEmails.length) {
        const confirmed = window.confirm("Duplicate emails were found. Do you want to continue with this import?");
        if (!confirmed) {
          return [
            "User rejected import due to duplicate emails."
          ];
        }
      }

      const errors = await fnQuery({
        variables: {
          input
        }
      });

      return errors.data?.preValidateCsvImport ?? [];
    },
    [fnQuery, prefixOrders, storeId]
  );

  return useMemo(
    () => [
      fnWrappedQuery,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedQuery,
      tuple
    ]
  );
}



function __prepareForImport({
  rawCsvRows, data, storeId, prefixOrders
}: {
  rawCsvRows: Array<_.Dictionary<string>>;
  data: IData;
  storeId: string;
  prefixOrders: boolean;
}): ImportOrdersFromCsvInput {
  const rowsAsPropertyName2Value: Partial<Record<OrderPropertyName, string>>[] = _.map(rawCsvRows, row => {
    const transformedOrder = _.mapValues(
      data.propertyName2SourceLabel,
      (sourceLabel: string, propName: OrderPropertyName) =>
        row[sourceLabel] || data.propertyName2DefaultValue[propName]);
    return transformedOrder;
  });

  // Add order prefix.
  if (prefixOrders) {
    _.forEach(rowsAsPropertyName2Value, row => {
      row.order_identifier = data.orderPrefix + '_' + row.order_identifier;
    });
  }

  const groupedRows = _.groupBy(rowsAsPropertyName2Value, row => row.order_identifier);
  const orders: ImportOrderInput[] = _.map(groupedRows, (rawRows): ImportOrderInput => {
    const lineItemPropNames: OrderPropertyName[] = [
      'lineItem_sku',
      'lineItem_quantity',
      'lineItem_itemTotalDollars',
      'lineItem_taxTotalDollars',
      'lineItem_feeTotalDollars'
    ];

    // Assumed here that the order details are the same for each "line item" row.
    const orderDetailRow = _.first(rawRows);

    if (!orderDetailRow) {
      alert("ERROR ON IMPORT");
      throw new Error('ERROR_CONVERTING');
    }

    const detailOrderKeys = _.pickBy(orderDetailRow, (val, key) => !_.includes(lineItemPropNames, key));
    return {
      detail: _.map(detailOrderKeys, (val, key): ImportPropertyValue => ({
        propertyName: key as OrderPropertyName,
        value: val
      })),
      lineItems: _.map(rawRows, (row): ImportOrderLineItemInput => {
        const relevantLineItemKeys = _.pickBy(row, (r, key) => _.includes(lineItemPropNames, key));
        return {
          values: _.map(relevantLineItemKeys, (val, key): ImportPropertyValue => ({
            propertyName: key as OrderPropertyName,
            value: val ?? ''
          }))
        };
      })
    };
  });

  return {
    storeId,
    orders
  };
}
